// Données mockées pour le Module d'Objectifs et de Performance (KPI Tracking)

export type KPIType = 'reports_count' | 'survey_results' | 'projects_completed' | 'brand_visibility' | 'customer_satisfaction' | 'custom';
export type KPIStatus = 'on_track' | 'at_risk' | 'off_track' | 'exceeded';
export type KPIPeriod = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

export interface KPIDataSource {
  type: 'reports' | 'surveys' | 'projects' | 'custom';
  source_id?: string;
  filter?: Record<string, any>;
}

export interface KPI {
  id: string;
  name: string;
  description?: string;
  type: KPIType;
  target_value: number;
  current_value: number;
  unit: string; // '%', 'nombre', '€', etc.
  period: KPIPeriod;
  start_date: string;
  end_date: string;
  status: KPIStatus;
  progress_percentage: number;
  data_source: KPIDataSource;
  created_by: string;
  created_by_name: string;
  created_at: string;
  updated_at: string;
  linked_project_id?: string;
  linked_project_name?: string;
  zone?: string;
  category?: string;
}

export interface AgentPerformance {
  agent_id: string;
  agent_name: string;
  role: string;
  total_reports: number;
  completed_projects: number;
  survey_responses: number;
  kpi_score: number;
  achievements: string[];
  rank: number;
}

export interface TeamPerformance {
  team_id: string;
  team_name: string;
  total_kpis: number;
  kpis_on_track: number;
  kpis_at_risk: number;
  kpis_off_track: number;
  average_progress: number;
  rank: number;
}

export const mockKPIs: KPI[] = [
  {
    id: 'kpi-001',
    name: 'Augmenter la visibilité de la marque SOBEBRA de 30% dans la zone Cotonou Centre',
    description: 'Objectif trimestriel basé sur les activations et la présence en points de vente',
    type: 'brand_visibility',
    target_value: 30,
    current_value: 24.5,
    unit: '%',
    period: 'quarterly',
    start_date: '2024-10-01',
    end_date: '2024-12-31',
    status: 'on_track',
    progress_percentage: 81.7,
    data_source: {
      type: 'projects',
      filter: { zone: 'Cotonou Centre', status: 'completed' },
    },
    created_by: 'soebra-001',
    created_by_name: 'SOBEBRA - Direction Marketing',
    created_at: '2024-10-01T10:00:00Z',
    updated_at: '2024-11-15T10:00:00Z',
    linked_project_id: '1',
    linked_project_name: 'Campagne Activation Point de Vente - Cotonou Centre',
    zone: 'Cotonou Centre',
    category: 'Visibilité Marque',
  },
  {
    id: 'kpi-002',
    name: 'Collecter 150 réponses aux sondages consommateurs',
    description: 'Objectif de collecte de données pour le trimestre Q4 2024',
    type: 'survey_results',
    target_value: 150,
    current_value: 135,
    unit: 'réponses',
    period: 'quarterly',
    start_date: '2024-10-01',
    end_date: '2024-12-31',
    status: 'on_track',
    progress_percentage: 90,
    data_source: {
      type: 'surveys',
      source_id: 'survey-001',
    },
    created_by: 'agence-001',
    created_by_name: 'Agence Zenith Productions',
    created_at: '2024-10-01T10:00:00Z',
    updated_at: '2024-11-15T10:00:00Z',
    linked_project_id: '2',
    linked_project_name: 'Sondage Consommateurs - Marchés de Cotonou',
    category: 'Collecte de Données',
  },
  {
    id: 'kpi-003',
    name: 'Soumettre 50 rapports d\'activation terrain',
    description: 'Objectif mensuel de reporting pour suivre les activités terrain',
    type: 'reports_count',
    target_value: 50,
    current_value: 42,
    unit: 'rapports',
    period: 'monthly',
    start_date: '2024-11-01',
    end_date: '2024-11-30',
    status: 'at_risk',
    progress_percentage: 84,
    data_source: {
      type: 'reports',
      filter: { status: 'submitted', month: 11 },
    },
    created_by: 'agence-001',
    created_by_name: 'Agence Zenith Productions',
    created_at: '2024-11-01T10:00:00Z',
    updated_at: '2024-11-15T10:00:00Z',
    category: 'Reporting',
  },
  {
    id: 'kpi-004',
    name: 'Terminer 5 projets d\'activation ce trimestre',
    description: 'Objectif de complétion des projets BTL',
    type: 'projects_completed',
    target_value: 5,
    current_value: 3,
    unit: 'projets',
    period: 'quarterly',
    start_date: '2024-10-01',
    end_date: '2024-12-31',
    status: 'on_track',
    progress_percentage: 60,
    data_source: {
      type: 'projects',
      filter: { status: 'completed' },
    },
    created_by: 'soebra-001',
    created_by_name: 'SOBEBRA - Direction Marketing',
    created_at: '2024-10-01T10:00:00Z',
    updated_at: '2024-11-15T10:00:00Z',
    category: 'Projets',
  },
  {
    id: 'kpi-005',
    name: 'Atteindre 85% de satisfaction client',
    description: 'Basé sur les résultats des sondages consommateurs',
    type: 'customer_satisfaction',
    target_value: 85,
    current_value: 78.5,
    unit: '%',
    period: 'quarterly',
    start_date: '2024-10-01',
    end_date: '2024-12-31',
    status: 'at_risk',
    progress_percentage: 92.4,
    data_source: {
      type: 'surveys',
      filter: { question_type: 'satisfaction' },
    },
    created_by: 'soebra-001',
    created_by_name: 'SOBEBRA - Direction Marketing',
    created_at: '2024-10-01T10:00:00Z',
    updated_at: '2024-11-15T10:00:00Z',
    category: 'Satisfaction Client',
  },
  {
    id: 'kpi-006',
    name: 'Déployer 10 stands promotionnels en zone Akpakpa',
    description: 'Objectif de déploiement matériel pour Q4',
    type: 'projects_completed',
    target_value: 10,
    current_value: 8,
    unit: 'stands',
    period: 'quarterly',
    start_date: '2024-10-01',
    end_date: '2024-12-31',
    status: 'on_track',
    progress_percentage: 80,
    data_source: {
      type: 'projects',
      source_id: '3',
    },
    created_by: 'agence-001',
    created_by_name: 'Agence Zenith Productions',
    created_at: '2024-10-01T10:00:00Z',
    updated_at: '2024-11-15T10:00:00Z',
    linked_project_id: '3',
    linked_project_name: 'Déploiement Matériel Promotionnel - Zone Akpakpa',
    zone: 'Akpakpa',
    category: 'Déploiement',
  },
];

export const mockAgentPerformances: AgentPerformance[] = [
  {
    agent_id: 'agent-001',
    agent_name: 'Koffi Amoussou',
    role: 'agent_terrain',
    total_reports: 28,
    completed_projects: 2,
    survey_responses: 45,
    kpi_score: 92,
    achievements: ['Top Performer Novembre', '100% Rapports à temps', 'Excellence Activation'],
    rank: 1,
  },
  {
    agent_id: 'agent-002',
    agent_name: 'Marie Adékambi',
    role: 'agent_terrain',
    total_reports: 22,
    completed_projects: 1,
    survey_responses: 135,
    kpi_score: 88,
    achievements: ['Champion Sondages', 'Rapidité Collecte'],
    rank: 2,
  },
  {
    agent_id: 'agent-003',
    agent_name: 'Jean Sègbè',
    role: 'agent_terrain',
    total_reports: 18,
    completed_projects: 1,
    survey_responses: 32,
    kpi_score: 75,
    achievements: ['Efficacité Déploiement'],
    rank: 3,
  },
];

export const mockTeamPerformances: TeamPerformance[] = [
  {
    team_id: 'team-agence',
    team_name: 'Agence Zenith Productions',
    total_kpis: 6,
    kpis_on_track: 4,
    kpis_at_risk: 2,
    kpis_off_track: 0,
    average_progress: 81.4,
    rank: 1,
  },
  {
    team_id: 'team-soebra',
    team_name: 'SOBEBRA - Direction Marketing',
    total_kpis: 3,
    kpis_on_track: 2,
    kpis_at_risk: 1,
    kpis_off_track: 0,
    average_progress: 78.0,
    rank: 2,
  },
];

export const getStatusColor = (status: KPIStatus): 'success' | 'warning' | 'error' | 'info' => {
  switch (status) {
    case 'on_track':
      return 'success';
    case 'at_risk':
      return 'warning';
    case 'off_track':
      return 'error';
    case 'exceeded':
      return 'info';
    default:
      return 'info';
  }
};

export const getStatusLabel = (status: KPIStatus): string => {
  switch (status) {
    case 'on_track':
      return 'Sur la bonne voie';
    case 'at_risk':
      return 'À risque';
    case 'off_track':
      return 'Hors piste';
    case 'exceeded':
      return 'Objectif dépassé';
    default:
      return status;
  }
};

export const getStatusIcon = (status: KPIStatus): string => {
  switch (status) {
    case 'on_track':
      return '🟢';
    case 'at_risk':
      return '🟡';
    case 'off_track':
      return '🔴';
    case 'exceeded':
      return '⭐';
    default:
      return '⚪';
  }
};

export const calculateKPIStatus = (progress: number, daysRemaining: number, totalDays: number): KPIStatus => {
  const daysElapsed = totalDays - daysRemaining;
  const expectedProgress = (daysElapsed / totalDays) * 100;

  if (progress >= 100) return 'exceeded';
  if (progress >= expectedProgress * 0.9) return 'on_track';
  if (progress >= expectedProgress * 0.7) return 'at_risk';
  return 'off_track';
};

export const getPeriodLabel = (period: KPIPeriod): string => {
  switch (period) {
    case 'daily':
      return 'Quotidien';
    case 'weekly':
      return 'Hebdomadaire';
    case 'monthly':
      return 'Mensuel';
    case 'quarterly':
      return 'Trimestriel';
    case 'yearly':
      return 'Annuel';
    default:
      return period;
  }
};





